import React, { useEffect, useState } from 'react';
import { Alert, Button, Drawer, Typography, theme } from 'antd';
import { CheckOutlined, ClearOutlined, UndoOutlined } from '@ant-design/icons';
import { useSignaturePad } from './useSignaturePad';
import './Signature.scss';

const { Text } = Typography;

// Pantalla angosta (teléfono vertical) O baja (teléfono horizontal): en ambos casos
// el panel lateral de 360 px de alto no cabe, así que se usa pantalla completa y el
// lienzo ocupa el espacio que sobre. Solo el ancho no basta: un teléfono horizontal
// (844×390) pasa el breakpoint `md` pero sigue sin altura.
const CONSULTA_COMPACTA = '(max-width: 767px), (max-height: 600px)';

function useCompacto() {
  const [compacto, setCompacto] = useState(() => window.matchMedia(CONSULTA_COMPACTA).matches);
  useEffect(() => {
    const mq = window.matchMedia(CONSULTA_COMPACTA);
    const alCambiar = () => setCompacto(mq.matches);
    alCambiar();
    mq.addEventListener('change', alCambiar);
    return () => mq.removeEventListener('change', alCambiar);
  }, []);
  return compacto;
}

/**
 * Captura de firma en un Drawer: pantalla completa en móvil, panel lateral en
 * escritorio. El lienzo es siempre blanco (representa el papel): la firma se
 * exporta como PNG transparente en tinta negra y así se ve igual en los PDF.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {string} props.title - p. ej. "Firma del especialista".
 * @param {'online'|'queue'|'blocked'} props.modo
 *   - online: se sube a S3 al guardar.
 *   - queue: sin conexión pero el flujo sabe sincronizarla luego.
 *   - blocked: sin conexión y el flujo no puede sincronizarla → no se permite guardar.
 * @param {(blob: Blob) => Promise<void>} props.onSave - persiste; debe lanzar si falla.
 * @param {() => void} props.onClose
 */
function SignatureDrawer({ open, title, modo = 'online', onSave, onClose }) {
  const { token } = theme.useToken();
  const movil = useCompacto();
  const { canvasRef, hasInk, clear, undo, toBlob } = useSignaturePad();
  const [guardando, setGuardando] = useState(false);

  const bloqueada = modo === 'blocked';

  const guardar = async () => {
    setGuardando(true);
    try {
      await onSave(await toBlob());
    } finally {
      setGuardando(false);
    }
  };

  const vars = {
    '--firma-borde': token.colorBorder,
    '--firma-guia': token.colorTextQuaternary,
    '--firma-texto': token.colorTextTertiary,
    '--firma-radio': `${token.borderRadiusLG}px`,
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={title}
      placement={movil ? 'bottom' : 'right'}
      {...(movil ? { height: '100%' } : { width: 720 })}
      maskClosable={false}
      destroyOnHidden
      className="firma-drawer"
      styles={{
        body: {
          display: 'flex',
          flexDirection: 'column',
          gap: token.marginSM,
          padding: movil ? token.paddingSM : token.paddingLG,
          minHeight: 0,
        },
      }}
      footer={
        <div className="firma-acciones">
          <Button icon={<UndoOutlined />} onClick={undo} disabled={!hasInk || guardando}>
            Deshacer
          </Button>
          <Button icon={<ClearOutlined />} onClick={clear} disabled={!hasInk || guardando} danger>
            Limpiar
          </Button>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={guardar}
            loading={guardando}
            disabled={!hasInk || bloqueada}
            className="firma-acciones__guardar"
          >
            Guardar firma
          </Button>
        </div>
      }
    >
      {modo === 'queue' && (
        <Alert
          type="info"
          showIcon
          message="Sin conexión. La firma se guardará en este equipo y se enviará cuando vuelva la señal."
        />
      )}
      {bloqueada && (
        <Alert
          type="warning"
          showIcon
          message="Sin conexión. Esta firma necesita conexión a internet para guardarse."
        />
      )}
      <Text type="secondary">Firme con el dedo o con un lápiz dentro del recuadro.</Text>
      <Text type="secondary" className="firma-giro">
        Gire el teléfono para tener más espacio.
      </Text>
      <div className={`firma-area${movil ? '' : ' firma-area--escritorio'}`} style={vars}>
        <canvas ref={canvasRef} className="firma-canvas" aria-label="Área de firma" />
        {!hasInk && (
          <div className="firma-guia" aria-hidden="true">
            <span className="firma-guia__texto">✕ Firme aquí</span>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default SignatureDrawer;
